import React, { useEffect, useState, useCallback, Suspense, lazy } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { ToastProvider } from "react-toast-notifications";
import { MuiThemeProvider, createTheme } from "@material-ui/core";
import { Provider } from "react-redux";
import { store } from "Redux@Helpers";

import Loading from "./components/loading";
import Landing from "./views/landing";
import { actions } from "./redux/_actions";
import api from "./api";
import {
  applyThemeColors,
  createMuiThemeFromColors,
  DEFAULT_THEME,
  loadStoredTheme,
} from "./utils/themeColors";
import { captureReferralFromUrl } from "./utils/referral";

/* Code-split non-landing routes so homepage JS stays lean */
const Profile = lazy(() => import("./views/profile"));
const Secure = lazy(() => import("./views/secure"));
const Verify = lazy(() => import("./views/verify"));
const Home = lazy(() => import("./views/home"));
const Share = lazy(() => import("./views/share"));
const SingleShare = lazy(() => import("./views/singleshare"));
const JoinList = lazy(() => import("./views/JoinList"));
const SharedList = lazy(() => import("./views/SharedList"));
const Purchase = lazy(() => import("./views/purchase"));
const Order = lazy(() => import("./views/order"));
const ZincOrderStatus = lazy(() => import("./views/zincOrderStatus"));
const Social = lazy(() => import("./views/social"));
const AddItem = lazy(() => import("./views/AddItem"));
const Login = lazy(() => import("./views/Login"));
const Blog = lazy(() => import("./views/Blog"));
const BlogPost = lazy(() => import("./views/BlogPost"));
const Affiliate = lazy(() => import("./views/affiliate"));
const RetailerSignup = lazy(() => import("./views/retailer-signup"));
const RetailerClaim = lazy(() => import("./views/retailer-claim"));
const RetailerAnalytics = lazy(() => import("./views/retailer/analytics"));
const RetailerOrders = lazy(() => import("./views/retailer/orders"));
const RetailerRevenue = lazy(() => import("./views/retailer/revenue"));
const RetailerSettings = lazy(() => import("./views/retailer/settings"));
const Privacy = lazy(() => import("./views/service/privacy"));
const Support = lazy(() => import("./views/service/support"));
const Terms = lazy(() => import("./views/service/terms"));
const About = lazy(() => import("./views/about"));
const Contact = lazy(() => import("./views/service/contact"));
const AffiliateDisclosure = lazy(() => import("./views/service/affiliateDisclosure"));
const Personal = lazy(() => import("./views/personal"));
const ShoppingAssistant = lazy(() => import("./views/ShoppingAssistant"));
const UserContext = lazy(() => import("./views/UserContext"));
const ManageUsers = lazy(() => import("./views/admin/users"));
const ManageTaxes = lazy(() => import("./views/admin/taxes"));
const ManageCarts = lazy(() => import("./views/admin/carts"));
const ManageOrders = lazy(() => import("./views/admin/orders"));
const ManageAnalytics = lazy(() => import("./views/admin/analytics"));
const ManageFeedbacks = lazy(() => import("./views/admin/feedbacks"));
const ManageRetailers = lazy(() => import("./views/admin/retailers"));
const ManageAffiliateNetworks = lazy(() => import("./views/admin/affiliateNetworks"));
const StoresToMonetize = lazy(() => import("./views/admin/storesToMonetize"));
const ManageAIUsage = lazy(() => import("./views/admin/aiUsage"));

const ProtectedRoute = ({ isAdmin, isRetailer, ...props }) => {
  const { email, role } = useSelector((state) => state.auth);
  if (!email) {
    return (
      <Redirect key="redirect" to={`/login?redirect=${window.location.href}`} />
    );
  }
  if (isAdmin && role !== "admin") return <Redirect key="redirect" to="/" />;
  if (
    isRetailer &&
    role !== "retailer" &&
    role !== "hybrid" &&
    role !== "admin"
  ) {
    return <Redirect key="redirect" to="/" />;
  }
  return <Route {...props} />;
};

function AppContainer() {
  const dispatch = useDispatch();
  const { email, loading, verifyLoadingState } = useSelector(
    (state) => state.auth
  );

  const [interv, setInterv] = useState(null);
  const [muiTheme, setMuiTheme] = useState(() =>
    createMuiThemeFromColors(createTheme, loadStoredTheme() || DEFAULT_THEME)
  );

  const fetchRealTimeData = useCallback(() => {
    // reserved for real-time fetch
  }, []);

  useEffect(() => {
    const onTheme = (e) => {
      setMuiTheme(createMuiThemeFromColors(createTheme, e.detail));
    };
    window.addEventListener("ollacart-theme", onTheme);
    return () => window.removeEventListener("ollacart-theme", onTheme);
  }, []);

  useEffect(() => {
    captureReferralFromUrl();
    const token = localStorage.getItem("token");
    // Always resolve auth boot so we never stick on the full-screen loader
    dispatch(actions.verify(token));
  }, [dispatch]);

  useEffect(() => {
    if (!email) return;
    api
      .getAccountSettings()
      .then((data) => {
        if (data?.user?.theme) {
          applyThemeColors(data.user.theme);
        }
      })
      .catch(() => {});
  }, [email]);

  useEffect(() => {
    if (email) {
      if (interv) return;
    }
    return () => {
      if (interv) clearInterval(interv);
    };
  }, [email, interv, setInterv, fetchRealTimeData]);

  // Never leave the body scroll-locked from a stale mobile menu
  useEffect(() => {
    document.body.classList.remove("no-scroll");
  }, []);

  if (verifyLoadingState !== "finished") return <Loading />;

  return (
    <div>
      <MuiThemeProvider theme={muiTheme}>
        {/* Toasts clear themselves unless a caller passes autoDismiss: false. */}
        <ToastProvider placement="top-right" autoDismiss autoDismissTimeout={6000}>
          <Router>
            <Suspense fallback={<Loading />}>
              <Switch>
                <Route
                  path="/secure/:uid"
                  name="secure"
                  exact
                  component={Secure}
                />
                <Route
                  path="/verify/:uid"
                  name="verify"
                  exact
                  component={Verify}
                />
                <Route path="/privacy-policy" exact component={Privacy} />
                <Route path="/terms-of-service" exact component={Terms} />
                <Route path="/personal-data" exact component={Personal} />
                <Route path="/support" exact component={Support} />
                <Route path="/about" exact component={About} />
                <Route path="/contact" exact component={Contact} />
                <Route path="/affiliate-disclosure" exact component={AffiliateDisclosure} />
                <Route
                  path="/share/together/:productid"
                  name="singleshare"
                  exact
                  component={SingleShare}
                />
                <Route
                  path="/share/:userid"
                  name="share"
                  exact
                  component={Share}
                />
                <Route path="/list/:code" exact component={SharedList} />
                <ProtectedRoute path="/join/:code" exact component={JoinList} />
                <Route path="/blog" exact component={Blog} />
                <Route path="/blog/:slug" exact component={BlogPost} />
                <Route path="/retailer-signup" exact component={RetailerSignup} />
                <Route path="/retailer/claim" exact component={RetailerClaim} />
                <ProtectedRoute
                  isRetailer
                  path="/retailer"
                  exact
                  render={() => <Redirect to="/retailer/analytics" />}
                />
                <ProtectedRoute
                  isRetailer
                  path="/retailer/analytics"
                  exact
                  component={RetailerAnalytics}
                />
                <ProtectedRoute
                  isRetailer
                  path="/retailer/orders"
                  exact
                  component={RetailerOrders}
                />
                <ProtectedRoute
                  isRetailer
                  path="/retailer/revenue"
                  exact
                  component={RetailerRevenue}
                />
                <ProtectedRoute
                  isRetailer
                  path="/retailer/settings"
                  exact
                  component={RetailerSettings}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/users"
                  exact
                  component={ManageUsers}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/taxes"
                  exact
                  component={ManageTaxes}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/carts"
                  exact
                  component={ManageCarts}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/orders"
                  exact
                  component={ManageOrders}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/analytics"
                  exact
                  component={ManageAnalytics}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/feedbacks"
                  exact
                  component={ManageFeedbacks}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/retailers"
                  exact
                  component={ManageRetailers}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/affiliate-networks"
                  exact
                  component={ManageAffiliateNetworks}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/stores-to-monetize"
                  exact
                  component={StoresToMonetize}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/ai-usage"
                  exact
                  component={ManageAIUsage}
                />
                <ProtectedRoute
                  isAdmin
                  path="/admin/contracts"
                  exact
                  render={() => <Redirect to="/admin/retailers" />}
                />
                <ProtectedRoute path="/home" exact component={Home} />
                <ProtectedRoute path="/purchase" exact component={Purchase} />
                <ProtectedRoute path="/affiliate" exact component={Affiliate} />
                <ProtectedRoute path="/earnings" exact component={Affiliate} />
                <ProtectedRoute path="/social" exact component={Social} />
                <ProtectedRoute path="/order" exact component={Order} />
                <ProtectedRoute path="/zinc-order" exact component={ZincOrderStatus} />
                <ProtectedRoute path="/profile" exact component={Profile} />
                <ProtectedRoute path="/add" exact component={AddItem} />
                <ProtectedRoute
                  path="/assistant"
                  exact
                  component={ShoppingAssistant}
                />
                <ProtectedRoute
                  path="/assistant/context"
                  exact
                  component={UserContext}
                />
                {!email && (
                  <>
                    <Route
                      key="login"
                      path="/login"
                      name="login"
                      exact
                      component={Login}
                    />
                    <Route path="/" exact component={Landing} />
                  </>
                )}
                <Redirect key="redirect" to="/home" />
              </Switch>
            </Suspense>
          </Router>
          {loading && <Loading />}
        </ToastProvider>
      </MuiThemeProvider>
    </div>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <AppContainer />
    </Provider>
  );
}
